import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * `<stacked-rotary>` — concentric dual-ring rotary primitive.
 *
 * The defining control of colour-grading panels. Two coaxial caps share
 * one mechanical axis: the wider outer cap rotates one parameter, the
 * narrower inner cap (sitting on top) rotates another. Around the base
 * runs an illuminated skirt carrying the channel colour — on the
 * CGP-500's paint section the outer ring drives R / G / B Gain, the
 * inner ring drives R / G / B Black (or Flare, switched via the panel-
 * settings menu), and the channel illumination identifies the rotary
 * from peripheral vision.
 *
 * Top-down rendering layers, outside → inside:
 *
 *   r=48  chassis well (dark recess, fixed)
 *   r=44  LED ring outer bloom (channel colour, soft glow)
 *   r=41  LED ring inner sharp stroke
 *   r=38  outer cap skirt (the broader cylindrical base)
 *   r=34  outer cap top surface (annulus visible between r=22 and r=34)
 *   r=22  inner cap rim (subtle step where outer ends and inner starts)
 *   r=20  inner cap top surface (small disc that the operator drives
 *         independently of the outer)
 *
 * Each cap carries its own pointer stripe rendered as an HTML overlay
 * — bypasses the Chromium shadow-DOM rect-in-rotated-g bug that
 * collapses an SVG line's bounding box inside a rotated `<g>`.
 *
 * # Interaction
 *
 *   - Pointer-down inside the inner cap radius (< 42 % from centre) →
 *     vertical drag drives `innerRotation`.
 *   - Pointer-down outside that radius (on the outer cap annulus) →
 *     vertical drag drives `outerRotation`.
 *   - Mouse wheel respects the same inner / outer split based on
 *     pointer position.
 *
 * The split is purely visual / haptic; both axes are independent
 * digital encoders on the real hardware.
 *
 * # Theming
 *
 *   - `--sr-cap-outer` / `--sr-cap-inner` — cap surface colours
 *   - `--sr-skirt` — cylindrical base
 *   - `--sr-pointer` — engraved indicator stripe
 *   - `--sr-led` — illumination colour; falls back to `--re-led` so
 *     panel compositions that already drive the rotary-encoder LED
 *     ring via that variable work without further wiring
 *   - `--sr-detent` — knurl tick colour on the outer cap rim
 *
 * # Hardware reference
 *
 * Maintenance Manual 13-00089-000 v1.0, FRU list (p.7) — the rotaries
 * arrive as three pieces per encoder: `CGP KNOB ROTARY TOP` and `CGP
 * KNOB ROTARY BOTTOM` (10 each = 5 stacked pairs across the panel
 * body), driven by a single `CGP LIGHTGUIDE ROTARY` that conducts the
 * channel illumination from the LED board up through the base skirt.
 */
@customElement('stacked-rotary')
export class StackedRotaryElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        contain: layout paint;
        aspect-ratio: 1;
        inline-size: 100%;

        --sr-cap-outer: var(--control-cap, #2a2a2c);
        --sr-cap-outer-highlight:
          var(--control-cap-highlight, color-mix(in oklab, var(--sr-cap-outer) 55%, white));
        --sr-cap-inner:
          color-mix(in oklab, var(--sr-cap-outer) 80%, black);
        --sr-cap-inner-highlight:
          color-mix(in oklab, var(--sr-cap-inner) 50%, white);
        --sr-skirt: var(--control-skirt, #18181a);
        --sr-pointer: var(--control-pointer, #d8d8da);
        --sr-detent: var(--control-detent-line, #0a0a0b);
        --sr-led: var(--re-led, var(--encoder-led-colour, #5fa8ff));
      }

      svg {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        overflow: visible;
      }

      .rotary-stack {
        position: relative;
        inline-size: 100%;
        block-size: 100%;
        cursor: ns-resize;
        touch-action: none;
        user-select: none;
      }

      .rotary-stack:active {
        cursor: grabbing;
      }

      /* The channel illumination renders as a filled disc behind the
       * cap, not as a stroke. Filled SVG geometry styled with CSS
       * fill resolves var() reliably on both Blink and WebKit,
       * whereas SVG stroke-circle bboxes inside shadow DOM can
       * collapse to zero — which Chromium then skips painting. The
       * cap (drawn after, at r=34) covers the centre of the disc, so
       * what remains visible is a coloured annulus between r=34 and
       * the disc radius — exactly the LED skirt the operator sees
       * on the real hardware. */
      .led-halo {
        fill: var(--sr-led);
        opacity: 0.55;
        filter: url(#sr-glow);
      }

      .led-skirt {
        fill: var(--sr-led);
        opacity: 1;
      }

      .outer-cap,
      .inner-cap {
        transform-origin: 50px 50px;
        transition: transform 90ms ease-out;
      }

      /* Pointer stripes as HTML overlays. The Chromium shadow-DOM rect
       * bug collapses an SVG line nested inside a rotated <g> to a
       * zero-area bounding box and the line vanishes; HTML overlays
       * rotate with CSS transforms and stay measurable. */
      .pointer-overlay {
        position: absolute;
        background: var(--sr-pointer);
        border-radius: 40%;
        pointer-events: none;
        transform-origin: 50% 100%;
      }

      /* Outer pointer sits on the outer-cap top annulus — between the
       * inner cap edge (r ~ 22) and the outer cap edge (r ~ 34). At
       * rotation 0 it points straight up from the inner-cap rim
       * outward to the outer-cap edge. */
      .outer-pointer-overlay {
        left: 48.7%;
        top: 16%;
        inline-size: 2.6%;
        block-size: 20%;
      }

      /* Inner pointer sits on the inner-cap top disc. At rotation 0 it
       * points straight up from the cap centre to the cap edge. */
      .inner-pointer-overlay {
        left: 48.7%;
        top: 36%;
        inline-size: 2.6%;
        block-size: 14%;
      }
    `,
  ];

  /** Detents per revolution on the outer (Gain) ring. 0 = continuous. */
  @property({ type: Number, attribute: 'outer-detents' }) accessor outerDetents = 24;

  /** Detents per revolution on the inner (Black / Flare) ring. */
  @property({ type: Number, attribute: 'inner-detents' }) accessor innerDetents = 24;

  /** Whether the rotary ships with an addressable LED halo at the base. */
  @property({ type: Boolean, reflect: true, attribute: 'led-ring' }) accessor ledRing = false;

  /** Lit state of the LED halo. Off when ledRing is false. */
  @property({ type: Boolean, reflect: true }) accessor illuminated = false;

  /** Outer-ring rotation, degrees. */
  @property({ type: Number, attribute: 'outer-rotation' }) accessor outerRotation = 0;

  /** Inner-ring rotation, degrees. */
  @property({ type: Number, attribute: 'inner-rotation' }) accessor innerRotation = 0;

  /** Degrees of rotation per pixel of vertical drag. */
  @property({ type: Number, attribute: 'drag-sensitivity' }) accessor dragSensitivity = 1;

  /** Degrees per mouse-wheel notch. */
  @property({ type: Number, attribute: 'wheel-step' }) accessor wheelStep = 15;

  // Per-gesture state kept off the reactive surface so drags do not
  // re-render every move; the property changes the drag emits drive
  // the redraw.
  #dragPointerId = -1;
  #dragTarget: 'outer' | 'inner' | null = null;
  #dragStartClientY = 0;
  #dragStartRotation = 0;

  /**
   * Decide whether a pointer position falls on the inner cap or the
   * outer cap annulus. The threshold 0.42 (of half-width) matches the
   * inner-cap radius 22 vbu out of a 50 vbu half-viewBox.
   */
  #hitTarget(event: PointerEvent | WheelEvent): 'outer' | 'inner' {
    const rect = (event.currentTarget as Element).getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = event.clientX - cx;
    const dy = event.clientY - cy;
    const r = Math.hypot(dx, dy) / (rect.width / 2);
    return r < 0.42 ? 'inner' : 'outer';
  }

  #onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    this.#dragTarget = this.#hitTarget(event);
    this.#dragPointerId = event.pointerId;
    this.#dragStartClientY = event.clientY;
    this.#dragStartRotation = this.#dragTarget === 'inner' ? this.innerRotation : this.outerRotation;
    (event.currentTarget as Element).setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  #onPointerMove = (event: PointerEvent) => {
    if (event.pointerId !== this.#dragPointerId || !this.#dragTarget) return;
    const dy = event.clientY - this.#dragStartClientY;
    // Drag UP (negative dy) -> positive rotation (clockwise).
    const next = this.#dragStartRotation - dy * this.dragSensitivity;
    this.#applyRotation(this.#dragTarget, next);
  };

  #onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.#dragPointerId) return;
    this.#dragPointerId = -1;
    this.#dragTarget = null;
    const target = event.currentTarget as Element;
    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
  };

  #onWheel = (event: WheelEvent) => {
    event.preventDefault();
    const which = this.#hitTarget(event);
    const dir = event.deltaY > 0 ? -1 : 1;
    const current = which === 'inner' ? this.innerRotation : this.outerRotation;
    this.#applyRotation(which, current + dir * this.wheelStep);
  };

  #applyRotation(which: 'outer' | 'inner', target: number) {
    const detents = which === 'inner' ? this.innerDetents : this.outerDetents;
    const next = detents > 0
      ? Math.round(target / (360 / detents)) * (360 / detents)
      : target;
    if (which === 'inner') {
      if (next === this.innerRotation) return;
      this.innerRotation = next;
      this.dispatchEvent(new CustomEvent('inner-rotation-change', {
        bubbles: true, composed: true, detail: { rotation: next },
      }));
    } else {
      if (next === this.outerRotation) return;
      this.outerRotation = next;
      this.dispatchEvent(new CustomEvent('outer-rotation-change', {
        bubbles: true, composed: true, detail: { rotation: next },
      }));
    }
  }

  override render() {
    const showRing = this.ledRing && this.illuminated;
    return html`
      <div
        class="rotary-stack"
        @pointerdown=${this.#onPointerDown}
        @pointermove=${this.#onPointerMove}
        @pointerup=${this.#onPointerUp}
        @pointercancel=${this.#onPointerUp}
        @wheel=${this.#onWheel}
      >
        <svg viewBox="0 0 100 100" role="slider" aria-label="stacked dual-ring rotary">
          <defs>
            <radialGradient id="sr-outer-cap" cx="0.5" cy="0.4" r="0.55">
              <stop offset="0" stop-color="var(--sr-cap-outer-highlight)" />
              <stop offset="0.55" stop-color="var(--sr-cap-outer)" />
              <stop offset="1" stop-color="color-mix(in oklab, var(--sr-cap-outer) 55%, black)" />
            </radialGradient>
            <radialGradient id="sr-inner-cap" cx="0.5" cy="0.35" r="0.6">
              <stop offset="0" stop-color="var(--sr-cap-inner-highlight)" />
              <stop offset="0.55" stop-color="var(--sr-cap-inner)" />
              <stop offset="1" stop-color="color-mix(in oklab, var(--sr-cap-inner) 55%, black)" />
            </radialGradient>
            <radialGradient id="sr-skirt" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0.8" stop-color="var(--sr-skirt)" />
              <stop offset="1" stop-color="color-mix(in oklab, var(--sr-skirt) 35%, black)" />
            </radialGradient>
            <filter id="sr-glow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="2.4" />
            </filter>
          </defs>

          <!-- Chassis well -->
          <circle cx="50" cy="50" r="48" fill="color-mix(in oklab, var(--sr-skirt) 35%, black)" />

          ${showRing
            ? html`
                <!-- Bloomy halo and sharper skirt disc; the cap (drawn
                     after, at r=34) leaves an illuminated annulus
                     visible between the cap edge and the well. -->
                <circle class="led-halo" cx="50" cy="50" r="46" />
                <circle class="led-skirt" cx="50" cy="50" r="40" />
              `
            : ''}

          <!-- Thin shadow band at the cap base — reads as the gap
               between the cap underside and the illuminated chassis
               well; with the channel skirt below, the LED appears to
               leak up through this dark seam. -->
          <circle cx="50" cy="50" r="36" fill="color-mix(in oklab, var(--sr-skirt) 70%, black)" />

          <!-- Outer cap top surface. Rotates with outerRotation; the
               knurl marks read as the grip ridges around the rim. -->
          <g class="outer-cap" style="transform: rotate(${this.outerRotation}deg);">
            <circle cx="50" cy="50" r="34" fill="url(#sr-outer-cap)" />
            ${this.#renderKnurl(34, 28, 1.6)}
          </g>

          <!-- Step ring: a thin shadow ring between the outer cap top
               and the inner cap. Reads as the visible top edge of the
               outer cap's body where the inner cap sits proud of it. -->
          <circle
            cx="50"
            cy="50"
            r="22"
            fill="color-mix(in oklab, var(--sr-cap-outer) 60%, black)"
          />

          <!-- Inner cap. Smaller diameter sitting on top of the outer
               cap; rotates independently with innerRotation. -->
          <g class="inner-cap" style="transform: rotate(${this.innerRotation}deg);">
            <circle cx="50" cy="50" r="20" fill="url(#sr-inner-cap)" />
          </g>
        </svg>

        <!-- Pointer stripes rotate via CSS transform on HTML overlays -->
        <div
          class="pointer-overlay outer-pointer-overlay"
          style="transform: rotate(${this.outerRotation}deg);"
        ></div>
        <div
          class="pointer-overlay inner-pointer-overlay"
          style="transform: rotate(${this.innerRotation}deg);"
        ></div>
      </div>
    `;
  }

  /**
   * Render `count` tick marks evenly spaced around a ring, each a
   * short radial line pointing inward from the ring's circumference.
   * Used as the knurl grip on the outer cap rim.
   */
  #renderKnurl(radius: number, count: number, tickLength: number) {
    const ticks = [];
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * 2 * Math.PI;
      const outerX = 50 + Math.cos(angle) * radius;
      const outerY = 50 + Math.sin(angle) * radius;
      const innerX = 50 + Math.cos(angle) * (radius - tickLength);
      const innerY = 50 + Math.sin(angle) * (radius - tickLength);
      ticks.push(html`
        <line
          x1=${outerX}
          y1=${outerY}
          x2=${innerX}
          y2=${innerY}
          stroke="var(--sr-detent)"
          stroke-width="0.4"
          opacity="0.75"
        />
      `);
    }
    return ticks;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'stacked-rotary': StackedRotaryElement;
  }
}
